import { useState, useEffect, useCallback, useReducer, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useSpring, animated } from 'react-spring';
import { useDrag } from '@use-gesture/react';
import Spinner from 'react-spinkit';
import MarketingMoodboardMiddleSchool from './MarketingMoodboardMiddleSchool';
import Button from '../components/Button';
import SpinnerOverlay from '../SpinnerOverlay';
import ModalContainer from '../components/ModalContainer';
import MoodRow from './MoodRow';
import { RootState } from '../ApplicationState';
import { selectClassroom } from '../Classrooms';
import { isUserHasAnyKeys } from '../GlobalFunctions';
import {
  getMiddleSchoolJournals,
  isMixpanelQueryOnlyReportsEnabled,
  queryMixpanelByGroupV2,
} from '../BackendInterface';
import {
  getClassroomMoodScript,
  getGameProgressQueryStartDate,
  transformMoodEventsToMiddleSchoolJournalData,
} from '../AdministratorDashboard/reportUtilities';
import { values } from '../CheckIn/checkInValues';
import { InlineIcon } from '@iconify/react';
import arrowIosForwardFill from '@iconify/icons-eva/arrow-ios-forward-fill';
import arrowIosBackFill from '@iconify/icons-eva/arrow-ios-back-fill';
import circleCheck from '@iconify/icons-akar-icons/circle-check';
import banIcon from '@iconify-icons/ion/ban';
import styles from './MoodboardMiddleSchool.module.scss';
import CheckInToolMobile from '../components/CheckInToolMobile';
import CheckInTool from '../components/CheckInTool';
import { ClassroomType, StudentProxyType } from '@peekapak/types/DataProtocolTypes';
import { ClassJournalData, JournalData, MiddleSchoolCheckInEntry } from '@peekapak/types/ReportDataExchangeTypes';

interface ClassroomProps {
  classroomProps?: ClassroomType;
}

const MoodboardMiddleSchool = ({ classroomProps }: ClassroomProps): JSX.Element => {
  const today = new Date(new Date().setHours(0, 0, 0, 0));
  const [isShowStudents, setShowStudents] = useState(true);
  const [isShowWheelTipPopover, setShowWheelTipPopover] = useState(false);
  const wheelTipRef = useRef<HTMLSpanElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isShowWheelModal, setShowModal] = useState(false);
  const [selectedEmotion, setSelectedEmotion] = useState('');
  const [allStudentsLoaded, setAllStudentsLoaded] = useState(false);

  const [anchorDate, setAnchorDate] = useState(today);
  const [dateSpan, setDateSpan] = useState<Date[] | []>([]);
  const [springs, api] = useSpring(() => ({
    config: { mass: 0.3, tension: 300, friction: 10, clamp: true },
  }));

  const classDataReducer = (state: ClassJournalData, payload: JournalData | 'reset') => {
    if (payload === 'reset') return [];
    return [...state, payload];
  };

  const [classData, setClassData] = useReducer(classDataReducer, []);

  const isSmall = useMediaQuery({ maxWidth: 1080 });
  const isLarge = useMediaQuery({ minWidth: 1440 });

  const getDateSpan = useCallback(
    (date: Date) => {
      const dateArray = !isSmall
        ? Array(7)
            .fill(new Date(date))
            .map((el, i) => new Date(el.setDate(el.getDate() - el.getDay() + i + 1)))
        : Array(3)
            .fill(new Date(date))
            .map((_, i) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + (i - 2)));
      setDateSpan(dateArray);
    },
    [isSmall],
  );

  const isPeriodContainsToday = () => {
    const dateSpanCopy = dateSpan.map((date) => new Date(date));
    return today > dateSpan[0] && today < new Date(dateSpanCopy[dateSpanCopy.length - 1].setHours(23, 59, 59, 999));
  };

  const classroom = useSelector((state: RootState) => {
    const selectedClassroom = classroomProps ? classroomProps : selectClassroom(state);
    return selectedClassroom;
  });

  const userProfile = useSelector((state: RootState) => state.user.userProfile);

  const isLocked = !isUserHasAnyKeys(['KEY_MOODBOARD', 'KEY_PEEKAPAK_MANAGEMENT', 'KEY_WIP', 'KEY_MASTER_KEY']);

  const handleToggleNames = () => {
    setShowStudents((prevState) => !prevState);
  };

  const hideModal = () => setShowModal((prevState) => !prevState);

  const handleEmotionClick = (emotion: string) => {
    setSelectedEmotion(emotion);
  };

  const getClassJournalData = async (studentList: StudentProxyType[]) => {
    const useMixpanel = await isMixpanelQueryOnlyReportsEnabled(
      userProfile?.district || '',
      userProfile?.schoolId || '',
    );

    try {
      if (useMixpanel) {
        //
        // NEW PATH: single Mixpanel query for all students in the classroom.
        // journal-mood-log events are already tracked with string mood values.
        //
        const classroomId = classroom?.classroomId;
        if (!classroomId) return;

        const today = new Date().toISOString().split('T')[0];
        const fromDate = getGameProgressQueryStartDate('2023-08-01');
        const script = getClassroomMoodScript(classroomId, fromDate, today);
        const events = await queryMixpanelByGroupV2(script, 'unified');
        const allStudentsData: JournalData[] = transformMoodEventsToMiddleSchoolJournalData(events);

        // Merge Mixpanel results with the rostered student list so that
        // students with no events still appear as rows in the report.
        for (const student of studentList) {
          if (
            classData.some(
              (item) =>
                item?.studentName?.replace(/\s+/g, '') ===
                `${student.firstName} ${student.lastName || ''}`.replace(/\s+/g, ''),
            )
          ) {
            continue;
          }
          const studentName = `${student.firstName} ${student.lastName || ''}`.trim();
          const existingData = allStudentsData.find((d) => d.Progress.some((e) => e.userId === student.userId));
          const thisStudentData: JournalData = existingData ?? {
            firstName: student.firstName || '',
            lastName: student.lastName || '',
            studentName,
            Progress: [],
          };
          // Ensure studentName is set correctly from the roster
          thisStudentData.firstName = student.firstName || '';
          thisStudentData.lastName = student.lastName || '';
          thisStudentData.studentName = studentName;
          setClassData(thisStudentData);
        }
      } else {
        //
        // LEGACY PATH: per-student DynamoDB queries.
        // Retained for rollback capability behind the feature flag.
        //
        for (const student of studentList) {
          if (
            classData.some(
              (item) =>
                item?.studentName?.replace(/\s+/g, '') ===
                `${student.firstName} ${student.lastName || ''}`.replace(/\s+/g, ''),
            )
          )
            return null;
          const studentName = `${student.firstName} ${student.lastName || ''}`.trim();
          const studentJournalData: MiddleSchoolCheckInEntry[] = await getMiddleSchoolJournals(student.userId);
          const thisStudentData = {
            firstName: student.firstName || '',
            lastName: student.lastName || '',
            studentName: studentName,
            Progress: studentJournalData,
          };

          setClassData(thisStudentData);
        }
      }
    } finally {
      setAllStudentsLoaded(true);
    }
  };

  const loadStudentData = useCallback(async () => {
    try {
      classroom && getClassJournalData(classroom.listOfStudentProxies);
    } finally {
      setIsLoaded(true);
    }
  }, [classroom]);

  const handleClassChange = useCallback(() => {
    setAllStudentsLoaded(false);
    setClassData('reset');
    setIsLoaded(false);
    setShowStudents(true);
    loadStudentData();
  }, [loadStudentData]);

  const goToNextDateSet = () => {
    const newAnchorDate = new Date(anchorDate.setDate(anchorDate.getDate() + (isSmall ? 3 : 7)));

    api.start({
      config: { mass: 0.3, tension: 300, friction: 10, clamp: true },
      to: [
        { transform: 'translate3d(-100%, 0, 0)', opacity: 0 },
        { transform: 'translate3d(100%, 0, 0)', opacity: 0 },
        { transform: 'translate3d(0%, 0, 0)', opacity: 1 },
      ],
      from: { transform: 'translate3d(0%, 0, 0)', opacity: 1 },
    });
    setAnchorDate(newAnchorDate);
  };

  const goToPreviousDateSet = () => {
    const newAnchorDate = new Date(anchorDate.setDate(anchorDate.getDate() - (isSmall ? 3 : 7)));
    api.start({
      config: { mass: 0.3, tension: 300, friction: 10, clamp: true },
      to: [
        { transform: 'translate3d(100%, 0, 0)', opacity: 0 },
        { transform: 'translate3d(-100%, 0, 0)', opacity: 0 },
        { transform: 'translate3d(0%, 0, 0)', opacity: 1 },
      ],
      from: { transform: 'translate3d(0%, 0, 0)', opacity: 1 },
    });
    setAnchorDate(newAnchorDate);
  };

  useEffect(() => {
    handleClassChange();
  }, [classroom, handleClassChange]);

  useEffect(() => {
    getDateSpan(anchorDate);
  }, [isSmall, anchorDate, getDateSpan]);

  return isLocked ? (
    <MarketingMoodboardMiddleSchool key='noReports' />
  ) : (
    <div className={classroomProps ? styles.embedContainer : styles.container} key='hasAccess'>
      <SpinnerOverlay isShow={!isLoaded} />
      <div className={styles.titleBar}>
        <h2 className={styles.reportTitle}>Moodboard Check-in Data</h2>
        {anchorDate.toLocaleString}
        <Button small darkBlue thinText onClick={handleToggleNames}>
          {isShowStudents ? (
            <div className={styles.buttonText} key={'hide student button'}>
              Hide Student Info&nbsp;&nbsp;
              <InlineIcon icon={banIcon} width={24} />
            </div>
          ) : (
            <div className={styles.buttonText} key={'show student button'}>
              View Student Info&nbsp;&nbsp;
              <InlineIcon icon={circleCheck} width={24} />
            </div>
          )}
        </Button>
      </div>
      <div className={styles.subBar}>
        <div className={styles.pickerRow}>
          <h3>{isSmall ? 'Time Period:' : 'Week of:'}&nbsp;&nbsp;</h3>
          <button className={styles.navArrow} onClick={goToPreviousDateSet}>
            <InlineIcon icon={arrowIosBackFill} />
          </button>
          <div
            {...useDrag(
              ({ swipe: [swipeX] }) => {
                if (swipeX === 1) {
                  goToPreviousDateSet(); // swipe right
                } else if (swipeX === -1) {
                  goToNextDateSet(); // swipe left
                }
              },
              {
                swipe: { distance: 50, velocity: 0.4 },
              },
            )()}
          >
            &nbsp;{' '}
            {`${dateSpan[0]?.toLocaleDateString('en-us', {
              month: 'short',
            })} ${dateSpan[0]?.getDate()}`}{' '}
            -
            {` ${dateSpan[dateSpan.length - 1]?.toLocaleDateString('en-us', {
              month: 'short',
            })} ${dateSpan[dateSpan.length - 1]?.getDate()}`}
            &nbsp;
          </div>
          <button disabled={isPeriodContainsToday()} className={styles.navArrow} onClick={goToNextDateSet}>
            <InlineIcon icon={arrowIosForwardFill} />
          </button>
        </div>
        <div className={styles.feelingsRow}>
          <h3>Feelings categories</h3>
          <span className={styles.feelingsChevron}>›</span>
        </div>
        <div className={styles.emotionLegend}>
          {['Angry', 'Sad', 'Surprised', 'Confident', 'Happy', 'Calm'].map((title) => {
            const emotion = values.find((v) => v.title === title);
            const index = values.findIndex((v) => v.title === title);
            return emotion ? (
              <div
                className={styles.emotionBox}
                style={{
                  backgroundColor: emotion.segmentColor,
                  color: index < 2 ? '#43484d' : '#fff',
                }}
                key={title}
              >
                {emotion.title}
              </div>
            ) : null;
          })}
          {!isSmall && (
            <span className={styles.wheelTipWrapper} ref={wheelTipRef}>
              <span className={styles.wheelTip} onClick={() => setShowWheelTipPopover((prev) => !prev)}>
                ?
              </span>
              {isShowWheelTipPopover && (
                <>
                  <div
                    style={{
                      position: 'fixed',
                      inset: 0,
                      zIndex: 99,
                    }}
                    onClick={() => setShowWheelTipPopover(false)}
                  />
                  <div className={styles.wheelTipPopover}>
                    <p>
                      These are the overarching moods of the check-in wheel.{' '}
                      <span
                        className={styles.wheelTipLink}
                        onClick={() => {
                          setShowWheelTipPopover(false);
                          setShowModal(true);
                        }}
                      >
                        Click here
                      </span>{' '}
                      to view the full check-in wheel and available moods students can choose from.
                    </p>
                    <div className={styles.wheelTipArrow} />
                  </div>
                </>
              )}
              {isShowWheelModal && (
                <ModalContainer hideModal={hideModal}>
                  <div className={styles.modalContainer}>
                    <button onClick={hideModal}>x</button>
                    {isSmall ? (
                      <CheckInToolMobile selectedEmotion={selectedEmotion} handleEmotionClick={handleEmotionClick} />
                    ) : (
                      <CheckInTool selectedEmotion={selectedEmotion} handleEmotionClick={handleEmotionClick} />
                    )}
                  </div>
                </ModalContainer>
              )}
            </span>
          )}
        </div>
      </div>

      <div className={styles.studentDataContainer}>
        <div className={styles.titleRow}>
          <div className={styles.nameCell}>
            <h4>{!isSmall ? 'Student Name' : 'Name'}</h4>
          </div>
          <button className={styles.navArrow} onClick={goToPreviousDateSet}>
            <InlineIcon icon={arrowIosBackFill} width={24} />
          </button>
          <div className={styles.hideOverflow}>
            <animated.div className={styles.days} style={springs}>
              {dateSpan.map((day: Date, index) => (
                <div key={`${day}${index}`} className={styles.titleCell}>
                  <h4>
                    {day.toLocaleDateString('en-us', {
                      weekday: isLarge ? 'long' : 'short',
                    })}
                    &nbsp;
                  </h4>
                </div>
              ))}
            </animated.div>
          </div>
          <button className={styles.navArrow} onClick={goToNextDateSet} disabled={isPeriodContainsToday()}>
            <InlineIcon icon={arrowIosForwardFill} width={24} />
          </button>
        </div>

        {!isLoaded ? (
          <div key='loading class data' className={styles.loadingData}>
            Loading class data...
          </div>
        ) : (
          classData?.map((studentData, index) => (
            <MoodRow
              studentName={
                !isSmall
                  ? studentData?.studentName
                  : `${studentData?.firstName} ${studentData?.lastName?.substring(0, 1)}`
              }
              isShowStudents={isShowStudents}
              data={studentData.Progress}
              key={`${studentData?.studentName}${index} dataRow`}
              dateRange={dateSpan}
            />
          ))
        )}
        {!allStudentsLoaded && (
          <div className={styles.loadingMoreStudents}>
            <Spinner />
            Loading additional students &nbsp;&nbsp;
          </div>
        )}
      </div>
    </div>
  );
};

export default MoodboardMiddleSchool;
